import React, { useState, useEffect, useCallback, useRef } from 'react';
import WorkCard from '../../../components/UI/WorkCard';
import WorkDetailModal, { WorkDetail } from '../../../components/Profile/work/WorkDetailModal';
import { AppealModal } from '../../../components/UI/AppealModal';
import { appealService, Appeal } from '../../../services/appealService';
import { useToast } from '../../../components/UI/Toast';
import { postService, WorkSummary } from '../../../services/postService';

const MAX_PINNED_WORKS = 5;

const getPinUnavailableReason = (
    work: Pick<WorkSummary, 'visibility' | 'status' | 'isPinned'>
) => {
    if (work.isPinned) {
        return null;
    }

    if (work.visibility === 'draft') {
        return '草稿作品不能置顶';
    }

    if (work.status === 'under_review') {
        return '审核中的作品暂不能置顶';
    }

    if (work.status === 'rejected') {
        return '违规作品暂不能置顶';
    }

    if (work.status && work.status !== 'active') {
        return '当前状态的作品暂不能置顶';
    }

    return null;
};

interface WorkGridsProps {
    works?: WorkSummary[];
    isLoading?: boolean;
    isOwnProfile?: boolean;
    userId?: string;
    includePendingUploads?: boolean;
    initialOpenWorkId?: string;
    fallbackAuthor?: {
        id: string;
        name: string;
        avatar: string;
    };
}

const WorkGrids: React.FC<WorkGridsProps> = ({
    works,
    isLoading = false,
    isOwnProfile = true,
    userId = '',
    includePendingUploads = false,
    initialOpenWorkId,
    fallbackAuthor
}) => {
    const [worksList, setWorksList] = useState<WorkSummary[]>(works || []);
    const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [appealPostId, setAppealPostId] = useState<string | null>(null);
    const [appealsMap, setAppealsMap] = useState<Record<string, Appeal>>({});
    const [, setPendingUploadsVersion] = useState(0);
    const autoOpenedWorkIdRef = useRef<string | null>(null);
    const { showToast, ToastWrapper } = useToast();

    const hasRenderableMedia = (work: WorkSummary) => (
        work.type === 'image'
            ? Boolean(work.thumbnailUrl || work.images?.length)
            : Boolean(work.thumbnailUrl || work.videoUrl)
    );

    const compareWorks = (a: WorkSummary, b: WorkSummary) => {
        if (a.isPinned && !b.isPinned) {
            return -1;
        }

        if (!a.isPinned && b.isPinned) {
            return 1;
        }

        if (a.isPinned && b.isPinned) {
            const pinnedDiff = (b.pinnedAt || 0) - (a.pinnedAt || 0);
            if (pinnedDiff !== 0) {
                return pinnedDiff;
            }
        }

        const aCreatedAt = a.createdAtTimestamp || Date.parse(a.createdAt || '') || 0;
        const bCreatedAt = b.createdAtTimestamp || Date.parse(b.createdAt || '') || 0;
        const createdAtDiff = bCreatedAt - aCreatedAt;
        if (createdAtDiff !== 0) {
            return createdAtDiff;
        }

        return String(a.id).localeCompare(String(b.id));
    };

    const loadWorks = useCallback(async () => {
        if (Array.isArray(works)) {
            setWorksList(works);
            return;
        }

        if (!userId) {
            setWorksList([]);
            return;
        }

        setIsFetching(true);
        try {
            const fetchedWorks = await postService.getCreatorPosts(userId);
            setWorksList(fetchedWorks);
        } catch (error) {
            console.error('Error loading works:', error);
            setWorksList([]);
        } finally {
            setIsFetching(false);
        }
    }, [userId, works]);

    useEffect(() => {
        void loadWorks();
    }, [loadWorks]);

    // Auto-check moderation status for under_review posts on page load
    useEffect(() => {
        if (!isOwnProfile || worksList.length === 0) return;
        const underReviewIds = worksList.filter(w => w.status === 'under_review').map(w => w.id);
        if (underReviewIds.length === 0) return;
        postService.checkModerationBatch(underReviewIds).then(results => {
            const changed = Object.entries(results).some(([, status]) => status !== 'under_review');
            if (changed) void loadWorks();
        }).catch(() => {});
    }, [isOwnProfile, worksList.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch user's appeals to show status on rejected posts
    useEffect(() => {
        if (!isOwnProfile) return;
        appealService.getMyAppeals().then(res => {
            if (res.success && res.data?.appeals) {
                const map: Record<string, Appeal> = {};
                res.data.appeals.forEach(a => { map[a.postId] = a; });
                setAppealsMap(map);
            }
        }).catch(() => {});
    }, [isOwnProfile]);

    useEffect(() => {
        const handleRefresh = () => {
            void loadWorks();
        };

        const handlePendingUploadsChanged = () => {
            setPendingUploadsVersion((prev) => prev + 1);
        };

        window.addEventListener('posts_updated', handleRefresh);
        window.addEventListener('interactions_updated', handleRefresh);
        window.addEventListener('pending_work_uploads_changed', handlePendingUploadsChanged);
        return () => {
            window.removeEventListener('posts_updated', handleRefresh);
            window.removeEventListener('interactions_updated', handleRefresh);
            window.removeEventListener('pending_work_uploads_changed', handlePendingUploadsChanged);
        };
    }, [loadWorks]);

    const createDetailFromSummary = useCallback((work: WorkSummary): WorkDetail => ({
        id: work.id,
        type: work.type,
        videoUrl: work.videoUrl,
        images: work.images,
        thumbnailUrl: work.thumbnailUrl,
        title: work.title,
        description: work.description,
        tags: work.tags || [],
        likes: work.likes,
        views: work.views,
        favorites: work.favorites,
        comments: [],
        commentCount: work.commentsCount,
        createdAt: work.createdAt,
        pinnedAt: work.pinnedAt,
        author: {
            id: fallbackAuthor?.id || work.creatorUserId,
            name: fallbackAuthor?.name || work.creatorUserId,
            avatar: fallbackAuthor?.avatar || ''
        },
        collaborators: work.collaborators,
        isPinned: work.isPinned,
        liked: work.liked,
        bookmarked: work.bookmarked,
        auditStatus: work.auditStatus,
        visibility: work.visibility,
        status: work.status
    }), [fallbackAuthor]);

    const handleWorkClick = useCallback(async (work: WorkSummary) => {
        setSelectedWork(createDetailFromSummary(work));

        const detail = await postService.getPostDetail(String(work.id));
        if (detail) {
            setSelectedWork((current) => {
                if (!current || current.id !== work.id) {
                    return current;
                }

                return {
                    ...detail,
                    videoUrl: detail.videoUrl || current.videoUrl,
                    images: detail.images?.length ? detail.images : current.images,
                    thumbnailUrl: detail.thumbnailUrl || current.thumbnailUrl,
                    author: {
                        id: detail.author.id || current.author.id,
                        name: detail.author.name || current.author.name,
                        avatar: detail.author.avatar || current.author.avatar
                    }
                };
            });
            return;
        }
    }, [createDetailFromSummary]);

    const handleCloseModal = () => {
        setSelectedWork(null);
    };

    const handleTogglePin = async (id: string | number) => {
        const target = worksList.find((item) => item.id === id);
        if (!target) return;

        const targetWithLatestState = selectedWork && selectedWork.id === id
            ? {
                ...target,
                visibility: selectedWork.visibility ?? target.visibility,
                status: selectedWork.status ?? target.status,
                isPinned: selectedWork.isPinned ?? target.isPinned
            }
            : target;

        if (!targetWithLatestState.isPinned) {
            const unavailableReason = getPinUnavailableReason(targetWithLatestState);
            if (unavailableReason) {
                showToast(unavailableReason, 'info');
                return;
            }

            const pinnedCount = worksList.filter((item) => item.isPinned).length;
            if (pinnedCount >= MAX_PINNED_WORKS) {
                showToast('最多只能置顶 5 个作品，请先取消一个已置顶作品', 'info');
                return;
            }
        }

        const response = targetWithLatestState.isPinned
            ? await postService.unpinPost(String(id))
            : await postService.pinPost(String(id));

        if (!response.success) {
            if (!targetWithLatestState.isPinned && response.statusCode === 409) {
                showToast('最多只能置顶 5 个作品，请先取消一个已置顶作品', 'info');
                return;
            }

            if (!targetWithLatestState.isPinned && response.statusCode === 403) {
                showToast(getPinUnavailableReason(targetWithLatestState) || '当前状态的作品暂不能置顶', 'info');
                return;
            }

            showToast(
                response.error || (targetWithLatestState.isPinned ? '取消置顶失败' : '置顶失败'),
                'error'
            );
            return;
        }

        const nextPinnedAt = targetWithLatestState.isPinned ? undefined : Date.now();

        setWorksList((prev) => prev
            .map((item) => (
                item.id === id
                    ? { ...item, isPinned: !targetWithLatestState.isPinned, pinnedAt: nextPinnedAt }
                    : item
            ))
            .sort(compareWorks)
        );

        if (selectedWork && selectedWork.id === id) {
            setSelectedWork({
                ...selectedWork,
                isPinned: !targetWithLatestState.isPinned,
                pinnedAt: nextPinnedAt
            });
        }

        window.dispatchEvent(new Event('posts_updated'));
        showToast(
            targetWithLatestState.isPinned ? '已取消置顶' : '作品已置顶',
            targetWithLatestState.isPinned ? 'info' : 'success'
        );
    };

    const handleDelete = async (id: string | number) => {
        const response = await postService.deletePost(String(id));
        if (!response.success) {
            showToast(response.error || '删除作品失败', 'error');
            throw new Error(response.error || '删除作品失败');
        }

        setWorksList((prev) => prev.filter((item) => item.id !== id));
        if (selectedWork?.id === id) {
            setSelectedWork(null);
        }

        showToast('作品已删除', 'success');
    };

    const pendingUploads = includePendingUploads && userId
        ? postService.getPendingWorkUploads(userId)
        : [];

    const pendingUploadsMap = new Map<string, WorkSummary>();
    pendingUploads.forEach((work) => {
        pendingUploadsMap.set(String(work.id), work);
    });

    const mergedWorksMap = new Map<string, WorkSummary>();
    worksList.forEach((work) => {
        if (!hasRenderableMedia(work) && !pendingUploadsMap.has(String(work.id))) {
            return;
        }
        mergedWorksMap.set(String(work.id), work);
    });
    pendingUploads.forEach((work) => {
        const key = String(work.id);
        const existing = mergedWorksMap.get(key);

        if (!existing) {
            mergedWorksMap.set(key, work);
            return;
        }

        if (work.uploadState && !hasRenderableMedia(existing)) {
            mergedWorksMap.set(key, { ...existing, ...work });
        }
    });

    const sortedWorks = Array.from(mergedWorksMap.values()).sort(compareWorks);
    const showLoading = isLoading || isFetching;

    useEffect(() => {
        if (!initialOpenWorkId || autoOpenedWorkIdRef.current === initialOpenWorkId) {
            return;
        }

        if (showLoading) {
            return;
        }

        const target = sortedWorks.find((work) => String(work.id) === String(initialOpenWorkId));
        autoOpenedWorkIdRef.current = initialOpenWorkId;

        if (target) {
            void handleWorkClick(target);
            return;
        }

        void (async () => {
            try {
                const detail = await postService.getPostDetail(String(initialOpenWorkId));
                if (detail) {
                    setSelectedWork(detail);
                }
            } catch (error) {
                console.error('Failed to open shared work:', error);
            }
        })();
    }, [handleWorkClick, initialOpenWorkId, showLoading, sortedWorks]);

    return (
        <>
            <ToastWrapper />
            {showLoading && sortedWorks.length === 0 ? (
                <div className="flex justify-center items-center py-20">
                    <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-black animate-spin"></div>
                </div>
            ) : sortedWorks.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 py-20 text-center text-sm text-gray-400">
                    暂无作品
                </div>
            ) : (
                <div className="grid grid-cols-[minmax(0,288px)] justify-center gap-x-4 gap-y-8 min-[640px]:grid-cols-[repeat(2,288px)] min-[960px]:grid-cols-[repeat(3,288px)] min-[1240px]:grid-cols-[repeat(4,288px)] min-[1520px]:grid-cols-[repeat(5,288px)]">
                    {sortedWorks.map((work) => (
                        <WorkCard
                            key={work.id}
                            work={work}
                            variant="profile"
                            onClick={() => void handleWorkClick(work)}
                            onAppeal={work.auditStatus === 'REJECTED' ? () => setAppealPostId(String(work.id)) : undefined}
                            appealStatus={appealsMap[String(work.id)]?.status || null}
                        />
                    ))}
                </div>
            )}

            {selectedWork && (
                <WorkDetailModal
                    work={selectedWork}
                    isOpen={!!selectedWork}
                    onClose={handleCloseModal}
                    isOwnProfile={isOwnProfile}
                    onTogglePin={handleTogglePin}
                    pinDisabledReason={(() => {
                        const unavailableReason = getPinUnavailableReason(selectedWork);
                        if (unavailableReason) {
                            return unavailableReason;
                        }

                        if (
                            !selectedWork.isPinned &&
                            worksList.filter((item) => item.isPinned).length >= MAX_PINNED_WORKS
                        ) {
                            return '最多只能置顶 5 个作品，请先取消一个已置顶作品';
                        }

                        return undefined;
                    })()}
                    onDelete={handleDelete}
                />
            )}

            {appealPostId && (
                <AppealModal
                    isOpen={!!appealPostId}
                    onClose={() => setAppealPostId(null)}
                    postId={appealPostId}
                    onSubmitted={() => {
                        showToast('申诉已提交，我们将尽快处理', 'success');
                        setAppealPostId(null);
                        // Refresh appeals map
                        appealService.getMyAppeals().then(res => {
                            if (res.success && res.data?.appeals) {
                                const map: Record<string, Appeal> = {};
                                res.data.appeals.forEach(a => { map[a.postId] = a; });
                                setAppealsMap(map);
                            }
                        }).catch(() => {});
                    }}
                />
            )}
        </>
    );
};

export default WorkGrids;
